// src/pages/TransactionsPage.js
// หน้า POS / ออเดอร์ใหม่ + ประวัติออเดอร์ + ปุ่มพิมพ์บิล + popup + ปรับ draft → paid
// เพิ่มฟีเจอร์: ส่วนลดต่อรายการ + สรุปส่วนลดในบิล + แก้ไขราคา/หน่วยต่อรายการ
// ปรับ UI เป็นธีมโมเดิร์นขาว-เทา-ชมพูอ่อน

import React, { useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { getCustomers, validateDiscountCode } from "../api";
import AuthContext from "../AuthContext";
import { useNotification } from "../NotificationContext";
import OrderDetailModal from "../components/OrderDetailModal";
import { getOrderStatusText, getStatusBadgeClass } from "../utils/statusFormatter";
import SkeletonLoader from "../components/SkeletonLoader";

const TransactionsPage = () => {
  const { user } = useContext(AuthContext);
  const { addNotification } = useNotification();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const location = useLocation();

  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [activeCategoryId, setActiveCategoryId] = useState("all");
  const [serviceSearch, setServiceSearch] = useState(""); // ค้นหาบริการ

  // ข้อมูลลูกค้า / โน้ต / รายการในบิล
  const [customerId, setCustomerId] = useState(null);
  const [customerMemberId, setCustomerMemberId] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [externalNote, setExternalNote] = useState("");
  // Deposit State
  const [customerDeposits, setCustomerDeposits] = useState([]);
  const [payDeposit, setPayDeposit] = useState(0);
  const [selectedDepositIds, setSelectedDepositIds] = useState([]); // Array of IDs selected for usage

  // Discount code campaign
  const [discountCode, setDiscountCode] = useState("");
  const [discountInfo, setDiscountInfo] = useState(null); // {valid,message,percent_off,amount_off,code}
  const [checkingCode, setCheckingCode] = useState(false);
  const [appliedCampaign, setAppliedCampaign] = useState(0); // snapshot after create

  // วันที่ของบิล (Admin สามารถเลือกวันย้อนหลัง/วันใดๆ ได้)
  const getTodayDateStr = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const [orderDate, setOrderDate] = useState(getTodayDateStr);

  // Customer Selection Modal
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  // VIP modal pagination (5 rows per page)
  const [customerPage, setCustomerPage] = useState(1);
  const CUSTOMER_PAGE_SIZE = 5;

  const [posItems, setPosItems] = useState([]); // แต่ละแถวในบิล
  const [posMobileTab, setPosMobileTab] = useState("services"); // "services" | "bill"
  const [saving, setSaving] = useState(false);

  // ✅ ป้องกันการกดสร้างบิลหรือชำระเงินรัวๆ (Synchronous double-click guard)
  const isSubmittingRef = useRef(false);
  const isPayingRef = useRef(false);

  // ✅ ความสูงเท่ากันเสมอระหว่างการ์ดเลือกบริการและการ์ดบิลปัจจุบัน (Height synchronization)
  const billCardRef = useRef(null);
  const [billCardHeight, setBillCardHeight] = useState(null);

  useEffect(() => {
    const updateHeight = () => {
      if (window.innerWidth >= 992 && billCardRef.current) {
        setBillCardHeight(billCardRef.current.offsetHeight);
      } else {
        setBillCardHeight(null);
      }
    };

    updateHeight();

    if (!billCardRef.current) return;

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        updateHeight();
      });
      resizeObserver.observe(billCardRef.current);
    }

    window.addEventListener("resize", updateHeight);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  // Edit mode for draft orders
  const [editMode, setEditMode] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);

  // ประวัติออเดอร์
  const [orderHistory, setOrderHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  // Query filters from URL
  const [filterItemName, setFilterItemName] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  // popup รายละเอียด
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);
  const [loadingOrderDetail, setLoadingOrderDetail] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Modal ยืนยันยกเลิกบิล (ตรงกลางจอ)
  const [cancelOrderModal, setCancelOrderModal] = useState({
    isOpen: false,
    order: null,
    isCanceling: false,
  });

  // นัดหมายวันนี้
  const [todayBookings, setTodayBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  // คำนวณเวลาที่เหลือจนถึงนัดหมาย
  const getTimeRemaining = (bookingDateTime) => {
    const now = new Date();
    const bookingTime = new Date(bookingDateTime);
    const diffMs = bookingTime - now;
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMs < 0) {
      return { text: "เลยเวลา", class: "text-danger fw-bold" };
    } else if (diffMins < 60) {
      return { text: `${diffMins}นาที`, class: diffMins <= 15 ? "text-danger fw-bold" : "text-warning" };
    } else {
      const diffHours = Math.floor(diffMins / 60);
      const remainingMins = diffMins % 60;
      return { text: `${diffHours}ชม.${remainingMins}น.`, class: diffHours < 1 ? "text-warning" : "text-success" };
    }
  };

  // ค้นหา + แบ่งหน้า ประวัติออเดอร์
  const [historySearch, setHistorySearch] = useState("");
  const [historyStatusFilter, setHistoryStatusFilter] = useState(""); // ฟิลเตอร์สถานะบิล
  const [currentHistoryPage, setCurrentHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10); // เริ่มต้นแสดง 10 รายการ

  // สถานะระหว่างรับชำระ draft
  const [payingOrderId, setPayingOrderId] = useState(null);
  // Payment modal state
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [payTargetOrder, setPayTargetOrder] = useState(null);
  const [payMode, setPayMode] = useState("existing"); // existing | current
  const [payMethod, setPayMethod] = useState("cash"); // cash | transfer | split
  const [payCash, setPayCash] = useState(0);
  const [payTransfer, setPayTransfer] = useState(0);
  const [payNote, setPayNote] = useState("");
  const [activePayInput, setActivePayInput] = useState('cash'); // 'cash' | 'transfer' | 'deposit'

  // โหลด categories, services, employees
  const loadInitialData = useCallback(async () => {
    try {
      const [catRes, svcRes, empRes] = await Promise.all([
        api.get("/service-categories"),
        api.get("/services"),
        api.get("/employees"),
      ]);
      setCategories(catRes.data || []);
      setServices(svcRes.data || []);
      setEmployees((empRes.data || []).filter((e) => e.is_active !== false));

      setActiveCategoryId("all");
    } catch (err) {
      addNotification(
        err.response?.data?.message || "โหลดข้อมูลบริการ/พนักงานไม่สำเร็จ",
        "danger"
      );
    }
  }, [addNotification]);

  // โหลดประวัติออเดอร์
  const loadOrderHistory = useCallback(async (keyword = null) => {
    try {
      setLoadingHistory(true);

      const params = new URLSearchParams();
      const searchKeyword =
        typeof keyword === "string" ? keyword : historySearch;

      if (searchKeyword && searchKeyword.trim()) {
        params.append("q", searchKeyword.trim());
      }
      if (filterDateFrom) params.append("date_from", filterDateFrom);
      if (filterDateTo) params.append("date_to", filterDateTo);
      if (filterItemName) params.append("item_name", filterItemName);

      const queryString = params.toString();
      const url = queryString ? `/orders?${queryString}` : "/orders";

      const res = await api.get(url);
      setOrderHistory(res.data || []);
      setCurrentHistoryPage(1);
    } catch (err) {
      addNotification(
        err.response?.data?.message || "โหลดประวัติออเดอร์ไม่สำเร็จ",
        "danger"
      );
    } finally {
      setLoadingHistory(false);
    }
  }, [historySearch, filterDateFrom, filterDateTo, filterItemName, addNotification]);

  const loadCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    try {
      const res = await getCustomers();
      setCustomers(res.data || []);
    } catch (err) {
      addNotification("โหลดข้อมูลลูกค้าไม่สำเร็จ", "danger");
    } finally {
      setLoadingCustomers(false);
    }
  }, [addNotification]);

  const handleOpenCustomerModal = () => {
    setShowCustomerModal(true);
    if (customers.length === 0) {
      loadCustomers();
    }
  };

  const handleSelectCustomer = (c) => {
    setCustomerId(c.id);
    setCustomerMemberId(c.member_id || null);
    setCustomerName(c.name);
    setCustomerPhone(c.phone || "");
    setShowCustomerModal(false);
    addNotification(`เลือกลูกค้า: ${c.name}`, "success");
    // Fetch deposits
    fetchCustomerDeposits(c.id);
  };

  const fetchCustomerDeposits = async (custId) => {
    try {
      const res = await api.get(`/customers/${custId}/deposits?active_only=true`);
      setCustomerDeposits(res.data || []);
    } catch (err) {
      console.error("Error loading deposits:", err);
    }
  };

  const totalDepositAvailable = useMemo(() => {
    return customerDeposits.reduce((sum, d) => sum + (Number(d.amount_remaining) || 0), 0);
  }, [customerDeposits]);

  // Derived: filter customers by search for VIP modal
  const filteredVipCustomers = React.useMemo(() => {
    const term = customerSearch.trim().toLowerCase();
    if (!term) return customers;
    return customers.filter((c) =>
      c.name.toLowerCase().includes(term) ||
      (c.phone && c.phone.includes(customerSearch)) ||
      (c.member_id && c.member_id.toLowerCase().includes(term))
    );
  }, [customers, customerSearch]);

  // Reset to page 1 on search text or when modal opens
  React.useEffect(() => { if (showCustomerModal) setCustomerPage(1); }, [customerSearch, showCustomerModal]);

  // Paging calculations
  const totalCustomerPages = Math.max(1, Math.ceil(filteredVipCustomers.length / CUSTOMER_PAGE_SIZE));
  const startVipIndex = (customerPage - 1) * CUSTOMER_PAGE_SIZE;
  const endVipIndex = Math.min(startVipIndex + CUSTOMER_PAGE_SIZE, filteredVipCustomers.length);
  const currentVipCustomers = React.useMemo(() => filteredVipCustomers.slice(startVipIndex, endVipIndex), [filteredVipCustomers, startVipIndex, endVipIndex]);

  // โหลดนัดหมายวันนี้
  const loadTodayBookings = useCallback(async () => {
    try {
      setLoadingBookings(true);
      // Use local date, not UTC
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const res = await api.get(`/bookings?date_from=${today}&date_to=${today}`);
      let bookings = res.data || [];

      // กรองเฉพาะนัดหมายที่ยังไม่เสร็จ และเรียงตามเวลา
      bookings = bookings
        .filter(booking => ['pending', 'confirmed'].includes(booking.status))
        .sort((a, b) => new Date(a.booking_datetime) - new Date(b.booking_datetime)) // เรียงตามเวลา
        .slice(0, 8); // แสดงสูงสุด 8 รายการ

      setTodayBookings(bookings);
    } catch (err) {
      console.error("Error loading bookings:", err);
      // ไม่ต้องแสดง notification เพราะเป็นส่วนเสริม
    } finally {
      setLoadingBookings(false);
    }
  }, []);

  useEffect(() => {
    // Read query params (on mount or when URL query changes)
    const sp = new URLSearchParams(location.search || "");
    const item = sp.get("item");
    const monthsStr = sp.get("months");
    const df = sp.get("date_from");
    const dt = sp.get("date_to");

    const today = new Date();
    const toLocalDateStr = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    let dateFrom = df || "";
    let dateTo = dt || "";
    if (monthsStr) {
      const months = Math.max(1, Math.min(12, parseInt(monthsStr, 10) || 0));
      const start = new Date(today.getFullYear(), today.getMonth() - (months - 1), 1);
      dateFrom = toLocalDateStr(start);
      if (!dateTo) {
        dateTo = toLocalDateStr(today);
      }
    }

    setFilterItemName(item || "");
    setFilterDateFrom(dateFrom);
    setFilterDateTo(dateTo);

    // load static data and today's bookings
    loadInitialData();
    loadTodayBookings();
  }, [location.search, loadInitialData, loadTodayBookings]);

  // Notify if customer has deposit
  useEffect(() => {
    if (totalDepositAvailable > 0) {
      addNotification(`ลูกค้ามีเงินมัดจำคงเหลือ ${formatMoney(totalDepositAvailable)} บาท`, "info");
    }
  }, [totalDepositAvailable, addNotification]);



  // Barcode Scanning Logic
  // Barcode Scanning Logic (Instant Match + Enter Key)
  // 1. Instant Match: If text matches barcode exactly, add immediately
  useEffect(() => {
    const term = serviceSearch.trim();
    if (!term) return;

    const scannedService = services.find(
      (s) => s.barcode && s.barcode.toLowerCase() === term.toLowerCase()
    );

    if (scannedService) {
      if (scannedService.is_active === false) {
        addNotification(`สินค้า "${scannedService.service_name}" ปิดใช้งานอยู่`, "warning");
        setServiceSearch("");
        return;
      }
      handleAddServiceToOrder(scannedService);
      setServiceSearch("");
    }
  }, [serviceSearch, services]);

  // 2. Enter Key: Fallback or for manual entry
  const handleServiceSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      const term = serviceSearch.trim();
      if (!term) return;
      // Logic is covered by useEffect for exact match, 
      // but we keep this if user presses Enter before useEffect triggers (rare)
      // or for future logic.
    }
  };

  // Re-load history when filter changes
  useEffect(() => {
    loadOrderHistory(historySearch);
  }, [historySearch, filterItemName, filterDateFrom, filterDateTo, loadOrderHistory]);

  const clearUrlFilters = () => {
    setFilterItemName("");
    setFilterDateFrom("");
    setFilterDateTo("");
    const base = window.location.pathname;
    window.history.replaceState({}, "", base);
  };

  // รวมยอดบิล: ก่อนไม่ส่วนลด / ส่วนลดรวม / สุทธิ
  const billTotals = useMemo(() => {
    let gross = 0; // รวมก่อนส่วนลด
    let discount = 0; // ส่วนลดรวม

    posItems.forEach((item) => {
      const qty = Number(item.qty) || 1;
      const unitPrice = Number(item.unit_price) || 0;
      const lineDiscount = Number(item.line_discount) || 0;

      gross += unitPrice * qty;
      discount += lineDiscount;
    });

    const net = Math.max(gross - discount, 0); // กันค่าติดลบ

    // Calculate campaign discount from discountInfo
    let campaignDisc = 0;
    if (discountInfo?.valid) {
      if (discountInfo.percent_off) {
        campaignDisc = net * (discountInfo.percent_off / 100.0);
      }
      if (discountInfo.amount_off) {
        campaignDisc += Number(discountInfo.amount_off) || 0;
      }
    }
    // Cap campaign discount
    campaignDisc = Math.min(campaignDisc, net);
    const finalTotal = Math.max(net - campaignDisc, 0);

    return {
      grossSubtotal: gross,
      totalLineDiscount: discount,
      netTotal: net,
      campaignDiscount: campaignDisc,
      finalTotal: finalTotal,
    };
  }, [posItems, discountInfo]);

  // Recalculate Deposit Usage when selection changes (Moved here to access billTotals)
  useEffect(() => {
    if (!isPayModalOpen) return;

    const totalDue = payMode === "existing" && payTargetOrder
      ? Number(payTargetOrder.total_amount) || 0
      : (Number(billTotals.finalTotal) || 0); // Use finalTotal (net of discount)

    // Calculate credit from selected deposits
    const selected = customerDeposits.filter(d => selectedDepositIds.includes(d.id));
    const credit = selected.reduce((sum, d) => sum + d.amount_remaining, 0);

    const use = Math.min(totalDue, credit);
    setPayDeposit(use);

    // Auto-update remaining amount to default method
    const remaining = Math.max(0, totalDue - use);

    if (payMethod === 'cash') {
      setPayCash(remaining);
      setPayTransfer(0);
    } else if (payMethod === 'transfer') {
      setPayTransfer(remaining);
      setPayCash(0);
    } else if (payMethod === 'split') {
      // In Split mode, prioritize keeping Transfer amount (if possible), adjust Cash
      // But Transfer cannot exceed Remaining
      let t = Number(payTransfer) || 0;
      if (t > remaining) t = remaining;

      setPayTransfer(t);
      setPayCash(Math.max(0, remaining - t));
    }
  }, [selectedDepositIds, isPayModalOpen, payMode, payTargetOrder, billTotals.finalTotal, customerDeposits, payMethod, setPayDeposit, setPayCash, setPayTransfer, payTransfer]);

  // ฟิลเตอร์ประวัติออเดอร์ตามสถานะ
  const filteredOrderHistory = useMemo(() => {
    if (!orderHistory) return [];
    if (historyStatusFilter === "") return orderHistory; // แสดงทั้งหมด
    return orderHistory.filter((o) => o.status === historyStatusFilter);
  }, [orderHistory, historyStatusFilter]);

  const totalHistoryPages = useMemo(() => {
    if (!filteredOrderHistory || filteredOrderHistory.length === 0) return 1;
    return Math.ceil(filteredOrderHistory.length / historyPageSize);
  }, [filteredOrderHistory, historyPageSize]);

  const pagedHistory = useMemo(() => {
    const startIndex = (currentHistoryPage - 1) * historyPageSize;
    return filteredOrderHistory.slice(startIndex, startIndex + historyPageSize);
  }, [filteredOrderHistory, currentHistoryPage, historyPageSize]);

  // นับจำนวนบิล draft ทั้งหมด
  const draftOrderCount = useMemo(() => {
    if (!orderHistory) return 0;
    return orderHistory.filter((o) => o.status === "draft").length;
  }, [orderHistory]);

  // เพิ่มรายการเมื่อคลิก card บริการ
  const handleAddServiceToOrder = (svc) => {
    const isProduct = (svc.item_type || 'service') === 'product';
    if (isProduct) {
      const stock = Number(svc.stock_qty ?? 0);
      // รวมจำนวนในตะกร้าปัจจุบันของสินค้านี้
      const inCart = posItems
        .filter((i) => i.service_id === svc.service_id && (i.item_type || 'service') === 'product')
        .reduce((sum, i) => sum + (Number(i.qty) || 0), 0);

      if (stock <= 0) {
        addNotification(`สินค้า "${svc.service_name}" สต็อกหมด`, 'warning');
        return;
      }
      if (inCart + 1 > stock) {
        addNotification(`สต็อกไม่พอ (เหลือ ${stock - inCart} ชิ้น)`, 'warning');
        return;
      }
    }
    setPosItems((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        service_id: svc.service_id,
        service_name: svc.service_name,
        item_type: svc.item_type || 'service',
        unit_price: svc.price, // เริ่มต้นจากราคา service แต่แก้ได้
        qty: 1,
        main_emp_id: "",
        assistant_emp_id: "",
        assistant_share_percent: "",
        line_discount: "", // ส่วนลดต่อรายการ (บาท)
      },
    ]);
    addNotification(`เพิ่ม "${svc.service_name}" เข้าออเดอร์แล้ว`, "success");
  };

  const handleChangeItemField = (id, field, value) => {
    // คุมไม่ให้จำนวนสินค้ามากเกินสต็อกคงเหลือ (รวมทุกแถวของสินค้าชนิดเดียวกัน)
    if (field === 'qty') {
      setPosItems((prev) => {
        const target = prev.find((it) => it.id === id);
        if (!target) return prev;
        const isProduct = (target.item_type || 'service') === 'product';
        let next = [...prev];
        if (isProduct) {
          const svc = services.find((s) => s.service_id === target.service_id);
          const stock = Number(svc?.stock_qty ?? 0);
          const othersSum = next
            .filter((it) => it.id !== id && it.service_id === target.service_id && (it.item_type || 'service') === 'product')
            .reduce((sum, it) => sum + (Number(it.qty) || 0), 0);
          let desired = Number(value);
          if (isNaN(desired)) desired = 0;
          // จำกัดไม่ให้เกินคงเหลือที่เหลือจากแถวอื่น
          const maxAllowed = Math.max(0, stock - othersSum);
          if (desired > maxAllowed) {
            addNotification(`สต็อกไม่พอ (ปรับเป็น ${maxAllowed})`, 'warning');
            desired = maxAllowed;
          }
          if (desired <= 0) {
            // ลบแถวถ้าจำนวนเป็น 0 หรือติดลบ
            return next.filter((it) => it.id !== id);
          }
          return next.map((it) => (it.id === id ? { ...it, qty: desired } : it));
        }
        // ไม่ใช่สินค้า: ทำงานปกติ แต่ยังคงขั้นต่ำ 1
        let desired = Number(value);
        if (isNaN(desired) || desired < 1) desired = 1;
        return next.map((it) => (it.id === id ? { ...it, qty: desired } : it));
      });
      return;
    }
    setPosItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleRemoveItem = (id) => {
    setPosItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleClearOrder = () => {
    setCustomerId(null);
    setCustomerMemberId(null);
    setCustomerName("");
    setCustomerPhone("");
    setInternalNote("");
    setExternalNote("");
    setDiscountCode("");
    setDiscountInfo(null);
    setAppliedCampaign(0);
    setPosItems([]);
    setOrderDate(getTodayDateStr());
    // Clear edit mode
    setEditMode(false);
    setEditingOrderId(null);
    setCustomerDeposits([]);
    setPayDeposit(0);
  };

  // Load draft order into form for editing
  const handleEditDraftOrder = async (order) => {
    try {
      // Fetch full order details
      const res = await api.get(`/orders/${order.order_id}`);
      const fullOrder = res.data;

      // Populate customer info
      if (fullOrder.customer_id) {
        setCustomerId(fullOrder.customer_id);
        fetchCustomerDeposits(fullOrder.customer_id);
      } else {
        setCustomerId(null);
        setCustomerDeposits([]);
      }
      setCustomerMemberId(fullOrder.customer_member_id || null);
      setCustomerName(fullOrder.customer_name || "");
      setCustomerPhone(fullOrder.customer_phone || "");
      setInternalNote(fullOrder.internal_note || "");
      setExternalNote(fullOrder.external_note || "");
      if (fullOrder.created_at) {
        setOrderDate(fullOrder.created_at.slice(0, 10));
      } else {
        setOrderDate(getTodayDateStr());
      }

      // Handle discount code
      if (fullOrder.discount_code) {
        setDiscountCode(fullOrder.discount_code);
        // Optionally validate the code again
        try {
          const codeRes = await validateDiscountCode({
            code: fullOrder.discount_code,
            customer_phone: fullOrder.customer_phone || null
          });
          setDiscountInfo(codeRes.data);
        } catch {
          setDiscountInfo(null);
        }
      } else {
        setDiscountCode("");
        setDiscountInfo(null);
      }

      // Convert transactions to posItems format
      const items = (fullOrder.transactions || []).map((txn) => ({
        id: Date.now() + Math.random(),
        service_id: txn.service_id,
        service_name: txn.service_name,
        item_type: txn.item_type || 'service',
        unit_price: txn.unit_price || 0,
        qty: txn.qty || 1,
        main_emp_id: txn.emp_id || "",
        assistant_emp_id: txn.extra_employees?.[0]?.emp_id || "",
        assistant_share_percent: txn.extra_employees?.[0]?.share_percent || "",
        line_discount: txn.line_discount || "",
      }));
      setPosItems(items);

      // Set edit mode
      setEditMode(true);
      setEditingOrderId(order.order_id);

      addNotification(`กำลังแก้ไขบิล ${order.order_code}`, "info");

      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      addNotification(
        err.response?.data?.message || "โหลดข้อมูลบิลไม่สำเร็จ",
        "danger"
      );
    }
  };


  const buildOrderPayload = (status, discountCodeOverride) => {
    const items = posItems.map((item) => {
      const qty = Number(item.qty) || 1;
      const unit_price = Number(item.unit_price) || 0;
      const extra_employees = [];

      if (item.assistant_emp_id) {
        let share = null;
        if (item.assistant_share_percent) {
          const num = Number(item.assistant_share_percent);
          if (!isNaN(num)) {
            share = num;
          }
        }
        extra_employees.push({
          emp_id: Number(item.assistant_emp_id),
          role: "assistant",
          share_percent: share,
        });
      }

      return {
        service_id: item.service_id,
        qty,
        unit_price,
        main_emp_id: item.main_emp_id ? Number(item.main_emp_id) : null,
        extra_employees,
        line_discount: item.line_discount ? Number(item.line_discount) : 0,
      };
    });

    return {
      customer_id: customerId || null,
      customer_member_id: customerMemberId || null,
      customer_name: customerName || null,
      customer_phone: customerPhone || null,
      internal_note: internalNote || null,
      external_note: externalNote || null,
      status,
      items,
      order_date: orderDate || null,
      discount_code: discountCodeOverride !== undefined
        ? discountCodeOverride
        : (discountInfo?.valid ? discountInfo.code : (discountCode.trim() || null)),
    };
  };

  const requestDiscountValidation = async () => {
    const code = discountCode.trim().toUpperCase();
    if (!code) {
      setDiscountInfo(null);
      return { valid: true, code: null };
    }
    setCheckingCode(true);
    try {
      const res = await validateDiscountCode({
        code,
        customer_phone: customerPhone || "",
        order_id: editMode ? editingOrderId : null,
      });
      setDiscountInfo(res.data);
      return res.data;
    } catch (err) {
      const invalid = { valid: false, message: err.response?.data?.message || "ตรวจสอบโค้ดผิดพลาด" };
      setDiscountInfo(invalid);
      return invalid;
    } finally {
      setCheckingCode(false);
    }
  };

  const handleValidateDiscount = async () => {
    const result = await requestDiscountValidation();
    if (!result?.valid) {
      addNotification(result?.message || "โค้ดไม่ถูกต้อง", "warning");
    } else if (result.code) {
      addNotification("โค้ดใช้ได้ ✓", "success");
    }
  };

  const handleSubmitOrder = async (status) => {
    if (isSubmittingRef.current || saving) return;
    if (posItems.length === 0) {
      addNotification("กรุณาเลือกรายการบริการอย่างน้อย 1 รายการ", "warning");
      return;
    }

    isSubmittingRef.current = true;
    setSaving(true);

    try {
      let validatedCode = null;
      if (discountCode.trim()) {
        const validation = await requestDiscountValidation();
        if (!validation?.valid) {
          addNotification(validation?.message || "โค้ดส่วนลดใช้ไม่ได้", "warning");
          return;
        }
        validatedCode = validation.code;
      }
      const payload = buildOrderPayload(status, validatedCode);
      let res;

      if (editMode && editingOrderId) {
        // UPDATE existing draft order
        res = await api.put(`/orders/${editingOrderId}`, payload);
        addNotification(
          `แก้ไขออเดอร์สำเร็จ เลขบิล: ${res.data.order_code || ""} (สถานะ: ${res.data.status})`,
          "success"
        );
      } else {
        // CREATE new order
        res = await api.post("/orders", payload);
        if (res.data?.campaign_discount) {
          setAppliedCampaign(res.data.campaign_discount);
        }
        addNotification(
          `บันทึกออเดอร์สำเร็จ เลขบิล: ${res.data.order_code || ""} (สถานะ: ${res.data.status})`,
          "success"
        );
      }

      handleClearOrder();
      await loadOrderHistory(historySearch);
    } catch (err) {
      addNotification(
        err.response?.data?.message || "บันทึกออเดอร์ไม่สำเร็จ",
        "danger"
      );
    } finally {
      setSaving(false);
      isSubmittingRef.current = false;
    }
  };

  const handleShowOrderDetail = async (orderId) => {
    setLoadingOrderDetail(true);
    setSelectedOrderDetail(null);
    setIsDetailModalOpen(true);
    try {
      const res = await api.get(`/orders/${orderId}`);
      setSelectedOrderDetail(res.data);
    } catch (err) {
      addNotification(
        err.response?.data?.message || "โหลดรายละเอียดออเดอร์ไม่สำเร็จ",
        "danger"
      );
    } finally {
      setLoadingOrderDetail(false);
    }
  };

  const handleCancelOrder = (order) => {
    if (!isAdmin) return;
    setCancelOrderModal({
      isOpen: true,
      order: order,
      isCanceling: false,
    });
  };

  const handleCloseCancelModal = () => {
    if (cancelOrderModal.isCanceling) return;
    setCancelOrderModal({ isOpen: false, order: null, isCanceling: false });
  };

  const handleConfirmCancelOrder = async () => {
    const order = cancelOrderModal.order;
    if (!order) return;
    try {
      setCancelOrderModal((prev) => ({ ...prev, isCanceling: true }));
      await api.post(`/orders/${order.order_id}/cancel`);
      await loadOrderHistory(historySearch);
      if (selectedOrderDetail?.order_id === order.order_id) {
        await handleShowOrderDetail(order.order_id);
      }
      addNotification(`ยกเลิกบิล ${order.order_code} สำเร็จ`, "success");
      setCancelOrderModal({ isOpen: false, order: null, isCanceling: false });
    } catch (err) {
      setCancelOrderModal((prev) => ({ ...prev, isCanceling: false }));
      addNotification(
        err.response?.data?.message || "ยกเลิกออเดอร์ไม่สำเร็จ",
        "danger"
      );
    }
  };

  const openPayModal = async (order) => {
    // Validate stock for products in this order before allowing payment
    try {
      const res = await api.get(`/orders/${order.order_id}`);
      const fullOrder = res.data;
      const products = (fullOrder.transactions || []).filter(t => (t.item_type || 'service') === 'product');

      if (products.length > 0) {
        // Reload services to get latest stock
        const svcRes = await api.get("/services");
        const currentServices = svcRes.data || [];

        const outOfStock = [];
        for (const p of products) {
          const svc = currentServices.find(s => s.service_id === p.service_id);
          const stock = Number(svc?.stock_qty || 0);
          const needed = p.qty || 0;
          if (stock < needed) {
            outOfStock.push(`${p.service_name} (ต้องการ ${needed}, คงเหลือ ${stock})`);
          }
        }

        if (outOfStock.length > 0) {
          addNotification(
            `ไม่สามารถรับชำระได้ สินค้าไม่พอ:\n${outOfStock.join(', ')}\n\nกรุณาแก้ไขบิลก่อนรับชำระ`,
            "danger"
          );
          return;
        }
      }
    } catch (err) {
      addNotification(err.response?.data?.message || "ตรวจสอบสต็อกไม่สำเร็จ", "warning");
      return;
    }

    setPayMode("existing");
    setPayTargetOrder(order);
    setPayMethod("cash");

    // Auto-select ALL deposits
    const initialDepositIds = customerDeposits.map(d => d.id);
    setSelectedDepositIds(initialDepositIds);

    // Calc initial amounts
    const totalDue = Number(order?.total_amount) || 0;
    const totalAvail = customerDeposits.reduce((sum, d) => sum + d.amount_remaining, 0);
    const useDep = Math.min(totalDue, totalAvail);

    setPayDeposit(useDep);
    setPayCash(Math.max(0, totalDue - useDep));
    setPayTransfer(0);
    setPayNote("");
    setIsPayModalOpen(true);
  };

  // Open modal for current in-progress cart (POS bill not yet saved)
  const openPayModalForCurrent = async () => {
    if (isSubmittingRef.current || saving || isPayModalOpen) return;
    if (posItems.length === 0) {
      addNotification("ยังไม่มีรายการในบิล", "warning");
      return;
    }

    let validatedDiscount = null;
    if (discountCode.trim()) {
      validatedDiscount = await requestDiscountValidation();
      if (!validatedDiscount?.valid) {
        addNotification(validatedDiscount?.message || "โค้ดส่วนลดใช้ไม่ได้", "warning");
        return;
      }
    }
    const netTotal = billTotals.netTotal;
    const campaignDiscount = validatedDiscount
      ? netTotal * (Number(validatedDiscount.percent_off) || 0) / 100
        + (Number(validatedDiscount.amount_off) || 0)
      : 0;
    const total = Math.max(netTotal - Math.min(campaignDiscount, netTotal), 0);

    // Validate stock for products in current cart
    const products = posItems.filter(item => (item.item_type || 'service') === 'product');
    if (products.length > 0) {
      const outOfStock = [];
      for (const p of products) {
        const svc = services.find(s => s.service_id === p.service_id);
        const stock = Number(svc?.stock_qty || 0);
        const needed = Number(p.qty || 0);
        // Also check other items in cart
        const totalInCart = posItems
          .filter(i => i.service_id === p.service_id && (i.item_type || 'service') === 'product')
          .reduce((sum, i) => sum + (Number(i.qty) || 0), 0);
        if (stock < totalInCart) {
          outOfStock.push(`${p.service_name} (ต้องการ ${totalInCart}, คงเหลือ ${stock})`);
        }
      }

      if (outOfStock.length > 0) {
        addNotification(
          `ไม่สามารถรับชำระได้ สินค้าไม่พอ: ${outOfStock.join(', ')}`,
          "danger"
        );
        return;
      }
    }

    setPayMode("current");
    setPayTargetOrder(null);
    setPayMethod("cash");

    // Auto-select ALL deposits
    const initialDepositIds = customerDeposits.map(d => d.id);
    setSelectedDepositIds(initialDepositIds);

    // Calc initial amounts
    const totalDue = Number(total) || 0;
    const totalAvail = customerDeposits.reduce((sum, d) => sum + d.amount_remaining, 0);
    const useDep = Math.min(totalDue, totalAvail);

    setPayDeposit(useDep);
    setPayCash(Math.max(0, totalDue - useDep));
    setPayTransfer(0);
    setPayNote("");
    setIsPayModalOpen(true);
  };

  const closePayModal = () => {
    setIsPayModalOpen(false);
    setPayTargetOrder(null);
  };

  const handleConfirmPay = async () => {
    if (isPayingRef.current || saving || payingOrderId) return;
    isPayingRef.current = true;

    let cash = Number(payCash) || 0;
    let transfer = Number(payTransfer) || 0;

    // Determine total due based on mode
    const totalDue = payMode === "existing" && payTargetOrder
      ? Number(payTargetOrder.total_amount) || 0
      : Number(billTotals.finalTotal) || 0;

    if (payMethod === "cash") {
      transfer = 0;
    } else if (payMethod === "transfer") {
      cash = 0;
      // Validation: Transfer cannot exceed total
      if (transfer > totalDue) {
        addNotification(`ยอดโอนห้ามเกินยอดสุทธิ (${formatMoney(totalDue)} ฿)`, "warning");
        isPayingRef.current = false;
        return;
      }
    } else if (payMethod === "split") {
      // Validation: Transfer portion cannot exceed total
      if (transfer > totalDue) {
        addNotification(`ยอดโอนห้ามเกินยอดสุทธิ (${formatMoney(totalDue)} ฿)`, "warning");
        isPayingRef.current = false;
        return;
      }
    }

    // Validation: Total Paid must be >= Total Due
    if (cash + transfer + Number(payDeposit) < totalDue) {
      addNotification(`ยอดชำระรวม (${formatMoney(cash + transfer + Number(payDeposit))}) ต้องไม่ต่ำกว่ายอดสุทธิ (${formatMoney(totalDue)} ฿)`, "warning");
      isPayingRef.current = false;
      return;
    }

    // Allocate Deposit Usage based on Selection
    const depositUsages = [];
    if (payDeposit > 0 && selectedDepositIds.length > 0) {
      let remainingToDeduct = Number(payDeposit);

      // Filter only selected deposits
      const selectedDeposits = customerDeposits.filter(d => selectedDepositIds.includes(d.id));

      // Sort by ID is usually fine for FIFO
      const sortedDeposits = [...selectedDeposits].sort((a, b) => a.id - b.id);

      for (const dep of sortedDeposits) {
        if (remainingToDeduct <= 0) break;
        const canUse = Math.min(dep.amount_remaining, remainingToDeduct);
        if (canUse > 0) {
          depositUsages.push({
            deposit_id: dep.id,
            amount: canUse
          });
          remainingToDeduct -= canUse;
        }
      }

      if (remainingToDeduct > 0.01) { // Floating point tolerance
        addNotification("ยอดมัดจำที่เลือกมากกว่ายอดคงเหลือจริง (โปรดรีเฟรช)", "error");
        isPayingRef.current = false;
        return;
      }
    }

    const payload = {
      payment_method: payMethod,
      cash_amount: cash,
      transfer_amount: transfer,
      payment_note: payNote || null,
      deposit_usages: depositUsages
    };
    try {
      if (payMode === "existing") {
        const order = payTargetOrder;
        if (!order) return;
        setPayingOrderId(order.order_id);
        const res = await api.post(`/orders/${order.order_id}/pay`, payload);
        addNotification(res.data?.message || "รับชำระสำเร็จ", "success");
        closePayModal();
        await loadOrderHistory(historySearch);
        await handleShowOrderDetail(order.order_id);
        loadInitialData(); // ✅ Reload stock
      } else {
        // current cart: UPDATE if in edit mode, CREATE new draft if not
        if (posItems.length === 0) {
          addNotification("ยังไม่มีรายการในบิล", "warning");
          return;
        }
        setSaving(true);

        let targetOrderId;

        if (editMode && editingOrderId) {
          // UPDATE existing draft order first
          const updatePayload = buildOrderPayload("draft");
          await api.put(`/orders/${editingOrderId}`, updatePayload);
          targetOrderId = editingOrderId;
          addNotification("บันทึกการแก้ไขสำเร็จ กำลังรับชำระ...", "info");
        } else {
          // CREATE new draft order
          const createPayload = buildOrderPayload("draft");
          const created = await api.post("/orders", createPayload);
          targetOrderId = created.data?.order_id;
          if (!targetOrderId) throw new Error("ไม่พบหมายเลขบิลหลังสร้าง");
        }

        setPayingOrderId(targetOrderId);
        const res = await api.post(`/orders/${targetOrderId}/pay`, payload);
        addNotification(res.data?.message || "บันทึกและรับชำระสำเร็จ", "success");
        closePayModal();
        handleClearOrder();
        await loadOrderHistory(historySearch);
        await handleShowOrderDetail(targetOrderId);
        loadInitialData(); // ✅ Reload stock
      }
    } catch (err) {
      addNotification(
        err.response?.data?.message || err.message || "รับชำระไม่สำเร็จ",
        "danger"
      );
    } finally {
      setSaving(false);
      setPayingOrderId(null);
      isPayingRef.current = false;
    }
  };

  const formatMoney = (val) => {
    const num = Number(val) || 0;
    return num.toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // แสดงช่องทางการชำระเงินในตารางประวัติ
  const renderPaymentInfo = (o) => {
    if (!o || o.status !== "paid") return <span className="text-muted">-</span>;
    const cash = Number(o.cash_amount || 0);
    const transfer = Number(o.transfer_amount || 0);
    let method = o.payment_method || null;
    if (!method) {
      if (cash > 0 && transfer > 0) method = "split";
      else if (cash > 0) method = "cash";
      else if (transfer > 0) method = "transfer";
    }
    const fmt = (n) => (Number(n) || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (method === "cash") {
      return <span className="badge bg-secondary">เงินสด {fmt(cash)} ฿</span>;
    }
    if (method === "transfer") {
      return <span className="badge text-dark" style={{ backgroundColor: '#f3e5ab' }}>โอน {fmt(transfer)} ฿</span>;
    }
    if (method === "split") {
      return (
        <div className="d-flex flex-wrap gap-1">
          <span className="badge bg-secondary">เงินสด {fmt(cash)} ฿</span>
          <span className="badge text-dark" style={{ backgroundColor: '#f3e5ab' }}>โอน {fmt(transfer)} ฿</span>
        </div>
      );
    }
    return <span className="text-muted">-</span>;
  };

  const employeeOptions = employees.map((e) => ({
    value: e.emp_id,
    label: e.emp_name,
  }));

  const handleHistorySearchClick = () => {
    loadOrderHistory(historySearch);
  };

  const handleHistorySearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      loadOrderHistory(historySearch);
    }
  };

  const handleResetHistorySearch = () => {
    setHistorySearch("");
    loadOrderHistory("");
  };

  const startItemIndex =
    filteredOrderHistory.length === 0
      ? 0
      : (currentHistoryPage - 1) * historyPageSize + 1;
  const endItemIndex =
    filteredOrderHistory.length === 0
      ? 0
      : Math.min(currentHistoryPage * historyPageSize, filteredOrderHistory.length);

  // Compute overdue bookings (past time + still pending/confirmed)
  const overdueBookings = useMemo(() => {
    const now = new Date();
    return todayBookings.filter(b => {
      const bookingTime = new Date(b.booking_datetime);
      const isPast = bookingTime < now;
      const isNotCompleted = ['pending', 'confirmed'].includes(b.status);
      return isPast && isNotCompleted;
    });
  }, [todayBookings]);

  return (
    <div className="dashboard-page pos-page">
      {/* Hero ด้านบน POS */}
      <div className="dashboard-hero mb-3">
        <div>
          <div className="dashboard-hero-title">
            <i className="bi bi-cart-check me-2"></i>รายการขาย
          </div>
          <div className="dashboard-hero-subtitle">
            เลือกบริการ เพิ่มรายการ คิดเงิน และบันทึกบิลของลูกค้า
          </div>
        </div>
        <button
          type="button"
          className="btn btn-ks-primary"
          style={{ color: '#ffffff' }}
          onClick={() => navigate("/dashboard")}
        >
          <i className="bi bi-house-door me-1"></i>ไปหน้า Dashboard
        </button>
      </div>

      {/* Alert: Overdue Bookings */}
      {overdueBookings.length > 0 && (
        <div className="alert alert-warning d-flex align-items-center mb-3" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2 fs-5"></i>
          <div className="flex-grow-1">
            <strong>มีนัดหมายที่เลยเวลาแล้ว {overdueBookings.length} รายการ</strong>
            <span className="ms-2 small">
              ({overdueBookings.map(b => b.customer_name).slice(0, 3).join(', ')}{overdueBookings.length > 3 ? '...' : ''})
            </span>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-warning ms-3"
            onClick={() => navigate('/appointments')}
          >
            <i className="bi bi-arrow-right-circle me-1"></i>ไปอัปเดตสถานะ
          </button>
        </div>
      )}

      {/* Mobile Segmented Switcher (<= 991px) */}
      <div className="d-lg-none mb-3 sticky-top pt-1 pb-1" style={{ top: "60px", zIndex: 1010, background: "var(--ks-bg-main, #fafaf8)" }}>
        <div className="btn-group w-100 p-1 bg-white rounded-3 shadow-sm border" role="group">
          <button
            type="button"
            className={`btn btn-sm ${posMobileTab === "services" ? "btn-dark fw-bold" : "btn-light text-muted"}`}
            onClick={() => setPosMobileTab("services")}
          >
            <i className="bi bi-grid-3x3-gap me-1"></i>รายการบริการ ({services.length})
          </button>
          <button
            type="button"
            className={`btn btn-sm position-relative ${posMobileTab === "bill" ? "btn-dark fw-bold" : "btn-light text-muted"}`}
            onClick={() => setPosMobileTab("bill")}
          >
            <i className="bi bi-receipt me-1"></i>บิลปัจจุบัน
            {posItems.length > 0 && (
              <span className="badge bg-danger ms-1">
                {posItems.reduce((acc, it) => acc + (Number(it.qty) || 1), 0)}
              </span>
            )}
            <span className="ms-1 fw-bold text-warning">฿{formatMoney(billTotals.finalTotal)}</span>
          </button>
        </div>
      </div>

      <div className="row mt-2">
        {/* ซ้าย: กล่องบริการ / หมวดหมู่ */}
        <div className={`col-lg-5 mb-3 ${posMobileTab === "bill" ? "d-none d-lg-block" : ""}`}>
          <div
            className="card card-ks-soft d-flex flex-column pos-services-card"
            style={billCardHeight ? { height: `${billCardHeight}px` } : undefined}
          >
            <div className="card-body d-flex flex-column flex-grow-1" style={{ minHeight: 0, overflow: "hidden" }}>
              <h5 className="card-title mb-2 fw-bold text-secondary">
                <i className="bi bi-grid-3x3-gap me-2"></i>เลือกบริการ
              </h5>

              {/* ช่องค้นหาบริการ */}
              <div className="mb-2">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="ค้นหาชื่อบริการ / สแกน Barcode เพิ่มลงบิลทันที..."
                  value={serviceSearch}
                  onChange={(e) => setServiceSearch(e.target.value)}
                  onKeyDown={handleServiceSearchKeyDown}
                />
              </div>

              <ul className="nav nav-tabs mb-2 flex-nowrap overflow-x-auto pos-category-tabs" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                <li className="nav-item">
                  <button
                    className={
                      "nav-link text-nowrap " + (activeCategoryId === "all" ? "active" : "")
                    }
                    onClick={() => setActiveCategoryId("all")}
                  >
                    ทั้งหมด
                  </button>
                </li>
                {categories.map((cat) => (
                  <li className="nav-item" key={cat.id}>
                    <button
                      className={
                        "nav-link text-nowrap " +
                        (String(activeCategoryId) === String(cat.id) ? "active" : "")
                      }
                      onClick={() => setActiveCategoryId(String(cat.id))}
                    >
                      {cat.name}
                    </button>
                  </li>
                ))}
              </ul>

              {/* Service cards */}
              <div
                className="row g-2 flex-grow-1 align-content-start pos-services-grid"
                style={{ overflowY: "auto", flex: "1 1 0%" }}
              >
                {services
                  .filter((s) =>
                    activeCategoryId === "all"
                      ? true
                      : String(s.category_id) === String(activeCategoryId)
                  )
                  .filter((s) =>
                    serviceSearch.trim() === ""
                      ? true
                      : (s.service_name?.toLowerCase().includes(serviceSearch.trim().toLowerCase()) ||
                        (s.barcode || '').toLowerCase().includes(serviceSearch.trim().toLowerCase()))
                  )
                  .map((svc) => {
                    const isProduct = (svc.item_type || 'service') === 'product';
                    const remain = Number(svc.stock_qty ?? 0);
                    const outOfStock = isProduct && remain <= 0;
                    const disabled = (svc.is_active === false) || outOfStock;

                    return (
                      <div className="col-6 col-sm-6 col-md-4" key={svc.service_id}>
                        <div
                          className={
                            "card h-100 border-0 pos-service-card" +
                            (disabled ? " bg-light" : "")
                          }
                          style={{
                            cursor: disabled ? "not-allowed" : "pointer",
                            opacity: disabled ? 0.5 : 1,
                            position: "relative",
                            overflow: "hidden",
                            aspectRatio: "1 / 1",
                          }}
                          onClick={() => {
                            if (!disabled) {
                              handleAddServiceToOrder(svc);
                            }
                          }}
                        >
                          {/* รูปภาพเต็ม */}
                          {svc.image_url && (
                            <img
                              src={svc.image_url}
                              alt={svc.service_name}
                              className="card-img-top"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                filter: disabled ? "grayscale(100%)" : "none",
                              }}
                            />
                          )}

                          {/* Overlay ดำด้านบน */}
                          <div
                            style={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: "60px",
                              background: "linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.7))",
                              display: "flex",
                              alignItems: "flex-end",
                              padding: "0.5rem",
                            }}
                          >
                            <h6
                              className="pos-service-name mb-0"
                              style={{
                                margin: 0,
                                width: "100%",
                              }}
                            >
                              {svc.service_name}
                            </h6>
                          </div>

                          {/* ราคาไว้มุมขวาบน */}
                          <div
                            style={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              backgroundColor: "#d4af37",
                              color: "#fff",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "0.85rem",
                              fontWeight: "600",
                              fontFamily: "var(--font-thai)",
                            }}
                          >
                            {Math.floor(svc.price)} ฿
                          </div>

                          {/* กลุ่มป้ายมุมซ้ายบน: ซ้อนแนวตั้งเพื่อไม่ให้ทับกัน */}
                          <div
                            style={{
                              position: "absolute",
                              top: 8,
                              left: 8,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              gap: 4,
                              zIndex: 2,
                            }}
                          >
                            {/* คอมมิชชั่น */}
                            <div
                              style={{
                                color: "#fff",
                                backgroundColor: "rgba(0, 0, 0, 0.4)",
                                padding: "2px 6px",
                                borderRadius: "3px",
                                fontSize: "0.7rem",
                                fontWeight: "500",
                                fontFamily: "var(--font-thai)",
                              }}
                            >
                              คอม: {svc.commission_rate}%
                            </div>
                            {/* สต็อกคงเหลือ (เฉพาะสินค้าและยังมีของ) */}
                            {isProduct && !outOfStock && (
                              <div
                                style={{
                                  backgroundColor: "rgba(13,110,253,.9)",
                                  color: "#fff",
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                }}
                                title={`คงเหลือ ${remain}`}
                              >
                                คงเหลือ {remain}
                              </div>
                            )}
                          </div>

                          {/* ข้อความ "สินค้าหมด" เมื่อปิดใช้งาน */}
                          {(disabled) && (
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(0, 0, 0, 0.5)",
                              }}
                            >
                              <div
                                style={{
                                  color: "#000",
                                  fontSize: "1.5rem",
                                  fontWeight: "bold",
                                  textAlign: "center",
                                  fontFamily: "var(--font-thai)",
                                  textShadow: "1px 1px 2px rgba(255,255,255,0.5)",
                                }}
                              >
                                {outOfStock ? 'หมด' : 'ปิดใช้งาน'}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                {services.length === 0 && (
                  <div className="col-12">
                    <div className="text-muted">
                      ยังไม่มีบริการในระบบ กรุณาเพิ่มในเมนู Services
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ขวา: บิลปัจจุบัน */}
        <div className={`col-lg-7 mb-3 ${posMobileTab === "services" ? "d-none d-lg-block" : ""}`}>

          <div className="card card-ks-soft" ref={billCardRef}>
            <div className="card-body">
              <h5 className="card-title mb-3 d-flex justify-content-between align-items-center">
                <span className="fw-bold text-secondary">
                  <i className="bi bi-receipt me-2"></i>บิลปัจจุบัน
                </span>
                <span className="badge bg-primary" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                  สุทธิ: {formatMoney(billTotals.finalTotal)} ฿
                </span>
              </h5>

              {/* ลูกค้า + วันที่บิล (Admin) */}
              <div className="row mb-2">
                <div className={isAdmin ? "col-md-5 mb-2" : "col-md-7 mb-2"}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="form-label small fw-semibold text-muted mb-0">
                      <i className="bi bi-person me-1"></i>ชื่อลูกค้า
                    </label>
                    <button
                      className="btn btn-sm py-0 px-2 text-white"
                      style={{ fontSize: "0.7rem", fontWeight: "bold", backgroundColor: "#d4af37", border: "none" }}
                      onClick={handleOpenCustomerModal}
                    >
                      <i className="bi bi-star-fill me-1"></i>VIP
                    </button>
                  </div>
                  <input
                    className="form-control form-control-sm"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="เช่น คุณกุ๊กกิ๊ก"
                  />
                </div>
                <div className={isAdmin ? "col-md-4 mb-2" : "col-md-5 mb-2"}>
                  <label className="form-label small fw-semibold text-muted mb-1">
                    <i className="bi bi-telephone me-1"></i>เบอร์โทรลูกค้า
                  </label>
                  <input
                    className="form-control form-control-sm"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="เช่น 08x-xxx-xxxx"
                  />
                </div>
                {isAdmin && (
                  <div className="col-md-3 mb-2">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label small fw-semibold text-muted mb-0">
                        <i className="bi bi-calendar-event me-1"></i>วันที่บิล
                      </label>
                      <span className="badge bg-warning text-dark" style={{ fontSize: "0.65rem" }}>Admin</span>
                    </div>
                    <input
                      type="date"
                      className="form-control form-control-sm border-primary"
                      value={orderDate}
                      onChange={(e) => setOrderDate(e.target.value)}
                      title="เลือกวันที่ของบิล (เฉพาะ Admin)"
                    />
                  </div>
                )}
              </div>

              {/* Discount Code Row */}
              <div className="row mb-2">
                <div className="col-md-12 mb-2">
                  <label className="form-label small fw-semibold text-muted mb-1">
                    <i className="bi bi-tag me-1"></i>โค้ดส่วนลด (Campaign)
                  </label>
                  <div className="input-group input-group-sm">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="เช่น VIP10, NEW50, SALE2024"
                      value={discountCode}
                      onChange={(e) => {
                        setDiscountCode(e.target.value.toUpperCase());
                        setDiscountInfo(null);
                      }}
                      onBlur={handleValidateDiscount}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={checkingCode || !discountCode.trim()}
                      onClick={handleValidateDiscount}
                    >
                      {checkingCode ? (
                        <span className="spinner-border spinner-border-sm" role="status"></span>
                      ) : (
                        <><i className="bi bi-check-circle me-1"></i>ตรวจสอบ</>
                      )}
                    </button>
                  </div>
                  {discountInfo && (
                    <div className="mt-1 small" style={{ minHeight: '20px' }}>
                      {discountInfo.valid ? (
                        <span className="text-success">
                          <i className="bi bi-check-circle-fill me-1"></i>
                          ใช้ได้: {discountInfo.percent_off ? `ลด ${discountInfo.percent_off}%` : ''}
                          {discountInfo.amount_off ? ` -${discountInfo.amount_off}฿` : ''}
                        </span>
                      ) : (
                        <span className="text-danger">
                          <i className="bi bi-x-circle-fill me-1"></i>
                          {discountInfo.message || 'โค้ดไม่ถูกต้อง'}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Member ID Display */}
              {customerMemberId && (
                <div className="alert alert-info py-1 px-2 mb-2 d-flex align-items-center" style={{ fontSize: '0.85rem' }}>
                  <i className="bi bi-card-heading me-2"></i>
                  <strong>Member ID:</strong> <span className="ms-2">{customerMemberId}</span>
                </div>
              )}

              {/* รายการในบิล */}
              <div
                className="mb-2"
                style={{ maxHeight: "340px", overflowY: "auto" }}
              >
                {posItems.length === 0 ? (
                  <div className="text-muted">
                    ยังไม่มีบริการในบิล เลือกจากด้านซ้ายเพื่อเริ่มเพิ่มรายการ หรือ สแกนบาร์โค้ดเพื่อเพิ่มสินค้าลงบิลทันที
                  </div>
                ) : (
                  <div className="table-responsive">
                  <table className="table table-sm align-middle pos-bill-table" style={{ minWidth: 620 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 100 }}>บริการ</th>
                        <th style={{ width: 120 }}>จำนวน</th>
                        <th style={{ width: 160 }}>พนักงานหลัก</th>
                        <th style={{ width: 170 }}>ผู้ช่วย</th>
                        <th style={{ width: 170 }}>ราคา/หน่วย</th>
                        <th style={{ width: 150 }}>ส่วนลด</th>
                        <th style={{ width: 40 }} className="text-end">
                          รวม
                        </th>
                        <th style={{ width: 40 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {posItems.map((item) => {
                        const qty = Number(item.qty) || 1;
                        const unitPrice = Number(item.unit_price) || 0;
                        const lineDiscount = Number(item.line_discount) || 0;
                        const lineTotal = Math.max(
                          unitPrice * qty - lineDiscount,
                          0
                        );

                        return (
                          <tr key={item.id}>
                            <td>
                              <div className="small fw-semibold">
                                {item.service_name}
                              </div>
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                min="1"
                                {...((item.item_type || 'service') === 'product' ? (() => {
                                  const svc = services.find((s) => s.service_id === item.service_id);
                                  const stock = Number(svc?.stock_qty ?? 0);
                                  const others = posItems
                                    .filter((it) => it.id !== item.id && it.service_id === item.service_id && (it.item_type || 'service') === 'product')
                                    .reduce((sum, it) => sum + (Number(it.qty) || 0), 0);
                                  const maxAllowed = Math.max(1, stock - others);
                                  return { max: maxAllowed };
                                })() : {})}
                                value={item.qty}
                                onChange={(e) =>
                                  handleChangeItemField(
                                    item.id,
                                    "qty",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td>
                              <select
                                className="form-select form-select-sm"
                                style={{ fontSize: "0.75rem" }}
                                value={item.main_emp_id || ""}
                                onChange={(e) =>
                                  handleChangeItemField(
                                    item.id,
                                    "main_emp_id",
                                    e.target.value
                                  )
                                }
                              >
                                <option value="">-</option>
                                {employeeOptions.map((emp) => (
                                  <option key={emp.value} value={emp.value}>
                                    {emp.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select
                                className="form-select form-select-sm"
                                style={{ fontSize: "0.75rem" }}
                                value={item.assistant_emp_id || ""}
                                onChange={(e) =>
                                  handleChangeItemField(
                                    item.id,
                                    "assistant_emp_id",
                                    e.target.value
                                  )
                                }
                              >
                                <option value="">-</option>
                                {employeeOptions.map((emp) => (
                                  <option key={emp.value} value={emp.value}>
                                    {emp.label}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="number"
                                className="form-control form-control-sm mt-1"
                                style={{ fontSize: "0.75rem" }}
                                placeholder="% ส่วนแบ่ง"
                                value={item.assistant_share_percent || ""}
                                onChange={(e) =>
                                  handleChangeItemField(
                                    item.id,
                                    "assistant_share_percent",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                min="0"
                                placeholder="บาท/หน่วย"
                                value={item.unit_price}
                                onChange={(e) =>
                                  handleChangeItemField(
                                    item.id,
                                    "unit_price",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                placeholder="บาท"
                                value={item.line_discount || ""}
                                onChange={(e) =>
                                  handleChangeItemField(
                                    item.id,
                                    "line_discount",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td className="text-end">
                              <span className="small">
                                {formatMoney(lineTotal)}
                              </span>
                            </td>
                            <td>
                              <button
                                className="btn btn-outline-danger btn-sm btn-ks-outline"
                                type="button"
                                onClick={() => handleRemoveItem(item.id)}
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>

              {/* สรุปส่วนลด / ยอดรวม */}
              {posItems.length > 0 && (
                <div className="mb-3 small text-end">
                  <div>
                    รวมก่อนส่วนลด:{" "}
                    <strong>{formatMoney(billTotals.grossSubtotal)} ฿</strong>
                  </div>
                  <div>
                    ส่วนลดรายการ (Line):{" "}
                    <strong className="text-danger">-{formatMoney(billTotals.totalLineDiscount)} ฿</strong>
                  </div>
                  {discountInfo?.valid && (
                    <div>
                      ส่วนลดแคมเปญ (Code):{" "}
                      <strong style={{ color: "#d4af37" }}>
                        {discountInfo.percent_off ? `-${discountInfo.percent_off}%` : ''}
                        {discountInfo.amount_off ? ` -${formatMoney(discountInfo.amount_off)} ฿` : ''}
                      </strong>
                    </div>
                  )}
                  {billTotals.campaignDiscount > 0 && (
                    <div>
                      ส่วนลดแคมเปญใช้จริง:{" "}
                      <strong className="text-success">-{formatMoney(billTotals.campaignDiscount)} ฿</strong>
                    </div>
                  )}
                  {totalDepositAvailable > 0 && (
                    <div className="mb-1" style={{ color: '#d4af37' }}>
                      <i className="bi bi-wallet2 me-1"></i>
                      เครดิตมัดจำคงเหลือ: <strong>{formatMoney(totalDepositAvailable)} ฿</strong>
                    </div>
                  )}
                  <div className="border-top pt-1 mt-1">
                    ยอดสุทธิ:{" "}
                    <strong className="text-success" style={{ fontSize: '1.1rem' }}>
                      {formatMoney(billTotals.finalTotal)} ฿
                    </strong>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="row">
                <div className="col-lg-6 mb-3">
                  <label className="form-label small fw-semibold text-muted">
                    <i className="bi bi-file-text me-1"></i>โน้ตภายใน{" "}
                    <span className="text-muted small">
                      (แสดงใน log เท่านั้น)
                    </span>
                  </label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                  />
                </div>
                <div className="col-lg-6 mb-3">
                  <label className="form-label small fw-semibold text-muted">
                    <i className="bi bi-chat-left-text me-1"></i>โน้ตสำหรับลูกค้า{" "}
                    <span className="text-muted small">
                      (แสดงบนบิล / ใบเสร็จ)
                    </span>
                  </label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    value={externalNote}
                    onChange={(e) => setExternalNote(e.target.value)}
                  />
                </div>
              </div>

              {/* ปุ่มบันทึก */}
              <div className="d-flex justify-content-between">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm btn-ks-outline"
                  onClick={handleClearOrder}
                  disabled={saving}
                >
                  {editMode ? "ยกเลิกการแก้ไข" : "ล้างบิล"}
                </button>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm btn-ks-outline"
                    disabled={saving || isSubmittingRef.current || posItems.length === 0}
                    onClick={() => handleSubmitOrder("draft")}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        กำลังบันทึก...
                      </>
                    ) : (
                      "บันทึกเป็นร่าง (พักไว้ก่อน)"
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ks-success btn-sm"
                    disabled={saving || isSubmittingRef.current || posItems.length === 0}
                    onClick={openPayModalForCurrent}
                  >
                    <i className="bi bi-check-circle me-1"></i>
                    บันทึกและรับชำระ (ชำระตอนนี้)
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Sticky Bottom Bar for Mobile when cart has items */}
      {posMobileTab === "services" && posItems.length > 0 && (
        <div className="d-lg-none pos-floating-bar">
          <div>
            <div className="small text-white-50">
              {posItems.reduce((acc, it) => acc + (Number(it.qty) || 1), 0)} รายการในบิล
            </div>
            <div className="fw-bold fs-5 text-warning">
              {formatMoney(billTotals.finalTotal)} ฿
            </div>
          </div>
          <button
            type="button"
            className="btn btn-warning fw-bold px-3 py-2 shadow-sm"
            onClick={() => setPosMobileTab("bill")}
          >
            ดูบิล / ชำระเงิน <i className="bi bi-arrow-right ms-1"></i>
          </button>
        </div>
      )}

      {/* ส่วนนัดหมายวันนี้ */}
      <div className="row mt-3">
        <div className="col-12">
          <div className="card card-ks-soft">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="bi bi-calendar-day me-2 text-secondary"></i>
                นัดหมายวันนี้
              </h5>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => navigate("/appointments")}
              >
                จัดการนัดหมาย
              </button>
            </div>
            <div className="card-body">
              {loadingBookings ? (
                <div className="text-center py-3">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">กำลังโหลด...</span>
                  </div>
                </div>
              ) : todayBookings.length === 0 ? (
                <div className="text-center py-3 text-muted">
                  <i className="bi bi-calendar-x fa-2x mb-2"></i>
                  <div>ไม่มีนัดหมายวันนี้</div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '12%' }}>เวลา</th>
                        <th style={{ width: '12%' }}>เวลาที่เหลือ</th>
                        <th style={{ width: '22%' }}>ลูกค้า</th>
                        <th style={{ width: '22%' }}>บริการ</th>
                        <th style={{ width: '17%' }}>พนักงาน</th>
                        <th style={{ width: '15%' }}>สถานะ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {todayBookings.slice(0, 8).map((booking) => (
                        <tr key={booking.id}>
                          <td className="fw-bold">
                            {new Date(booking.booking_datetime).toLocaleTimeString('th-TH', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                          <td>
                            {(() => {
                              const timeRemaining = getTimeRemaining(booking.booking_datetime);
                              return (
                                <span className={timeRemaining.class}>
                                  <i className="bi bi-hourglass-split me-1"></i>
                                  {timeRemaining.text}
                                </span>
                              );
                            })()}
                          </td>
                          <td>
                            <div>
                              <div className="fw-bold">{booking.customer_name}</div>
                              {booking.customer_phone && (
                                <small className="text-muted">
                                  <i className="bi bi-telephone me-1"></i>
                                  {booking.customer_phone}
                                </small>
                              )}
                            </div>
                          </td>
                          <td>{booking.service_name || '-'}</td>
                          <td>{booking.employee_name || '-'}</td>
                          <td>
                            {(() => {
                              const st = booking.status;
                              if (st === 'pending') {
                                return <span className="badge bg-secondary">ไม่มัดจำ</span>;
                              }
                              if (st === 'confirmed') {
                                return <span className="badge" style={{ background: '#d4af37', color: '#fff' }}>มัดจำ</span>;
                              }
                              if (st === 'completed') {
                                return <span className="badge bg-success">เข้ารับบริการแล้ว</span>;
                              }
                              if (st === 'canceled') {
                                return <span className="badge bg-danger">ยกเลิกนัด</span>;
                              }
                              if (st === 'noshow') {
                                return <span className="badge bg-secondary">ไม่มา</span>;
                              }
                              return <span className="badge bg-light text-dark">{st}</span>;
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {todayBookings.length > 5 && (
                    <div className="text-center mt-2">
                      <small className="text-muted">
                        แสดง 5 รายการแรก จากทั้งหมด {todayBookings.length} รายการ
                      </small>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ส่วนล่าง: ประวัติออเดอร์ (ตาราง + ค้นหา + แบ่งหน้า) */}
      <div className="card card-ks-soft mt-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h5 className="card-title mb-0 fw-bold text-secondary">
              <i className="bi bi-clock-history me-2"></i>ประวัติออเดอร์ล่าสุด
            </h5>
            <div className="d-flex gap-2 flex-wrap">
              <select
                className="form-select form-select-sm"
                style={{ width: 200 }}
                value={historyStatusFilter}
                onChange={(e) => {
                  setHistoryStatusFilter(e.target.value);
                  setCurrentHistoryPage(1); // รีเซ็ตหน้าเมื่อเปลี่ยน filter
                }}
              >
                <option value="">
                  ทั้งหมด
                  {draftOrderCount > 0 && (
                    <span style={{ color: "red" }}> (ค้างชำระ {draftOrderCount})</span>
                  )}
                </option>
                <option value="paid">ชำระแล้ว</option>
                <option value="draft">
                  ยังไม่ชำระ{" "}
                  {draftOrderCount > 0 && (
                    <span style={{ color: "red" }}>({draftOrderCount})</span>
                  )}
                </option>
                <option value="canceled">ยกเลิก</option>
              </select>
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ width: 260 }}
                placeholder="ค้นหา เลขบิล / ชื่อลูกค้า / เบอร์โทร..."
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                onKeyDown={handleHistorySearchKeyDown}
              />
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary btn-ks-outline"
                onClick={handleHistorySearchClick}
                disabled={loadingHistory}
              >
                ค้นหา
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary btn-ks-outline"
                onClick={handleResetHistorySearch}
                disabled={loadingHistory}
              >
                รีเซ็ต
              </button>
            </div>
          </div>

          {loadingHistory ? (
            <SkeletonLoader type="table" rows={8} columns={8} />
          ) : orderHistory.length === 0 ? (
            <div className="text-muted">
              ยังไม่มีออเดอร์ในระบบ เมื่อบันทึกออเดอร์แล้วจะแสดงที่นี่
            </div>
          ) : (
            <>
              <div className="table-responsive mb-2">
                <table className="table table-sm table-hover align-middle">
                  <thead>
                    <tr>
                      <th>เลขบิล</th>
                      <th>วันเวลา</th>
                      <th>ลูกค้า</th>
                      <th>เบอร์โทร</th>
                      <th className="text-end">ยอดรวม</th>
                      <th>ชำระผ่าน</th>
                      <th>สถานะ</th>
                      <th style={{ width: 300 }}>จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedHistory.map((o) => (
                      <tr key={o.order_id}>
                        <td style={{ cursor: "pointer" }} onClick={() => handleShowOrderDetail(o.order_id)}>
                          <span className="badge bg-light text-dark border">{o.order_code}</span>
                        </td>
                        <td>
                          <small>
                            {o.server_time
                              ? new Date(o.server_time).toLocaleString("th-TH")
                              : o.created_at
                                ? new Date(
                                  o.created_at
                                ).toLocaleString("th-TH")
                                : "-"}
                          </small>
                        </td>
                        <td>{o.customer_name || "-"}</td>
                        <td>{o.customer_phone || "-"}</td>
                        <td className="text-end">
                          {formatMoney(o.total_amount)} ฿
                        </td>
                        <td>{renderPaymentInfo(o)}</td>
                        <td>
                          {o.status === "paid" && (
                            <span className="badge bg-success">ชำระแล้ว</span>
                          )}
                          {o.status === "draft" && (
                            <span className="badge bg-secondary">ยังไม่ชำระ</span>
                          )}
                          {o.status === "canceled" && (
                            <span className="badge bg-danger">ยกเลิก</span>
                          )}
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-ks-outline"
                              onClick={() => handleShowOrderDetail(o.order_id)}
                            >
                              ดูรายละเอียด
                            </button>
                            {o.status === "draft" && (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-ks-outline"
                                  onClick={() => handleEditDraftOrder(o)}
                                >
                                  <i className="bi bi-pencil-square me-1"></i>
                                  แก้ไข
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-paid btn-sm"
                                  disabled={payingOrderId === o.order_id}
                                  onClick={() => openPayModal(o)}
                                >
                                  <i className="fas fa-money-bill-wave me-1"></i>
                                  รับชำระ
                                </button>
                              </>
                            )}
                            {isAdmin && o.status !== "canceled" && (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-ks-outline"
                                onClick={() => handleCancelOrder(o)}
                              >
                                ยกเลิกบิล
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center small mb-1 flex-wrap gap-2">
                <div>
                  แสดง{" "}
                  <strong>
                    {startItemIndex}-{endItemIndex}
                  </strong>{" "}
                  จากทั้งหมด <strong>{filteredOrderHistory.length}</strong> รายการ
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <select
                    className="form-select form-select-sm"
                    style={{ width: "150px" }}
                    value={historyPageSize}
                    onChange={(e) => {
                      setHistoryPageSize(Number(e.target.value));
                      setCurrentHistoryPage(1); // รีเซ็ตหน้าเมื่อเปลี่ยน page size
                    }}
                  >
                    <option value={5}>5 รายการ</option>
                    <option value={10}>10 รายการ</option>
                    <option value={20}>20 รายการ</option>
                    <option value={50}>50 รายการ</option>
                  </select>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary btn-ks-outline"
                    disabled={currentHistoryPage <= 1}
                    onClick={() =>
                      setCurrentHistoryPage((p) => Math.max(1, p - 1))
                    }
                  >
                    &laquo; ก่อนหน้า
                  </button>
                  <span>
                    หน้า {currentHistoryPage} / {totalHistoryPages}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary btn-ks-outline"
                    disabled={currentHistoryPage >= totalHistoryPages}
                    onClick={() =>
                      setCurrentHistoryPage((p) =>
                        Math.min(totalHistoryPages, p + 1)
                      )
                    }
                  >
                    ถัดไป &raquo;
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Popup Modal แสดงรายละเอียดออเดอร์ - ใช้ shared component */}
      <OrderDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        orderDetail={selectedOrderDetail}
        loading={loadingOrderDetail}
        payingOrderId={payingOrderId}
        onPay={openPayModal}
        formatMoney={formatMoney}
      />

      {/* Payment Modal */}
      {isPayModalOpen && (
        <div
          className="pos-modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            zIndex: 1050,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="card p-0"
            style={{ width: 700, maxWidth: "95%", display: 'flex', flexDirection: 'row', minHeight: '500px', maxHeight: '90vh' }}
          >
            {/* Left Side: Inputs & Info */}
            <div className="p-3 d-flex flex-column" style={{ width: '50%', borderRight: '1px solid #eee' }}>
              <h6 className="mb-2 fw-bold">
                {payMode === "existing" && payTargetOrder
                  ? `รับชำระบิล ${payTargetOrder.order_code}`
                  : "บันทึกและรับชำระบิลใหม่"}
              </h6>

              <div className="alert alert-light border mb-3 text-center py-2">
                {(() => {
                  const valTotal = Number(payMode === "existing" && payTargetOrder ? payTargetOrder.total_amount : billTotals.finalTotal) || 0;
                  const valDeposit = Number(payDeposit) || 0;
                  const valDue = Math.max(0, valTotal - valDeposit);

                  if (valDeposit > 0) {
                    return (
                      <>
                        <div className="d-flex justify-content-between px-3 small text-muted">
                          <span>ยอดรวมบิล:</span>
                          <span>{formatMoney(valTotal)} ฿</span>
                        </div>
                        <div className="d-flex justify-content-between px-3 small text-success fw-bold">
                          <span>หักมัดจำ:</span>
                          <span>-{formatMoney(valDeposit)} ฿</span>
                        </div>
                        <hr className="my-1 opacity-50" />
                        <small className="text-muted d-block mt-1">ยอดที่ต้องชำระเพิ่ม</small>
                        <span className="fs-3 fw-bold text-primary">
                          {formatMoney(valDue)} ฿
                        </span>
                      </>
                    );
                  }
                  return (
                    <>
                      <small className="text-muted d-block">ยอดสุทธิที่ต้องชำระ</small>
                      <span className="fs-3 fw-bold text-primary">
                        {formatMoney(valTotal)} ฿
                      </span>
                    </>
                  );
                })()}
              </div>

              {/* Deposit Section (Multi-Select) */}
              {totalDepositAvailable > 0 && (
                <div className="mb-3 p-2 border rounded bg-light">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="fw-bold text-primary mb-0">ใช้เงินมัดจำ ({selectedDepositIds.length} รายการ)</label>
                    <span className="badge bg-success">มี {formatMoney(totalDepositAvailable)} ฿</span>
                  </div>

                  {/* Validation Message: If deposits exist but none selected, show warning implied by "Must choose" */}
                  {selectedDepositIds.length === 0 && (
                    <div className="text-danger small mb-2">* กรุณาเลือกมัดจำอย่างน้อย 1 รายการ</div>
                  )}

                  <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                    {customerDeposits.map(d => (
                      <label key={d.id} className="list-group-item list-group-item-action d-flex gap-2 align-items-center p-2 border-bottom" style={{ cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          className="form-check-input mt-0"
                          checked={selectedDepositIds.includes(d.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSelectedDepositIds(prev => checked ? [...prev, d.id] : prev.filter(id => id !== d.id));
                          }}
                        />
                        <div className="flex-grow-1 lh-sm">
                          <div className="d-flex justify-content-between">
                            <strong>{new Date(d.created_at || d.date).toLocaleDateString('th-TH')}</strong>
                            <span className="text-success fw-bold">{formatMoney(d.amount_remaining)} ฿</span>
                          </div>
                          {d.note && <div className="text-muted small text-truncate" style={{ maxWidth: '200px' }}>{d.note}</div>}
                        </div>
                      </label>
                    ))}
                  </div>
                  <div className="mt-2 text-end text-primary fw-bold border-top pt-2">
                    ใช้มัดจำ: -{formatMoney(payDeposit)} ฿
                  </div>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label form-label-sm">วิธีชำระ</label>
                <select
                  className="form-select form-select-sm"
                  value={payMethod}
                  onChange={(e) => {
                    const next = e.target.value;
                    setPayMethod(next);
                    const totalDue = payMode === "existing" && payTargetOrder
                      ? Number(payTargetOrder.total_amount) || 0
                      : Number(billTotals.finalTotal) || 0;

                    const remaining = Math.max(0, totalDue - Number(payDeposit));

                    if (next === "cash") {
                      setPayCash(remaining);
                      setPayTransfer(0);
                      setActivePayInput('cash');
                    } else if (next === "transfer") {
                      setPayCash(0);
                      setPayTransfer(remaining);
                      setActivePayInput('transfer');
                    } else if (next === "split") {
                      setPayTransfer(0);
                      setPayCash(remaining);
                      setActivePayInput('transfer');
                    }
                  }}
                >
                  <option value="cash">เงินสด</option>
                  <option value="transfer">โอน</option>
                  <option value="split">เงินสด + โอน</option>
                </select>
              </div>

              {/* Inputs */}
              <div className="flex-grow-1">
                {payMethod === "split" && (
                  <div className="mb-3">
                    <label className="form-label form-label-sm">รับโอน (฿)</label>
                    <input
                      type="number"
                      className={`form-control form-control-lg text-end ${activePayInput === 'transfer' ? 'border-primary shadow-sm' : ''}`}
                      value={payTransfer}
                      onFocus={() => setActivePayInput('transfer')}
                      onChange={(e) => {
                        let val = Number(e.target.value);
                        const totalDue = payMode === "existing" && payTargetOrder
                          ? Number(payTargetOrder.total_amount) || 0
                          : Number(billTotals.finalTotal) || 0;

                        const deposit = Number(payDeposit) || 0;
                        const maxTransfer = Math.max(0, totalDue - deposit);

                        // Transfer cannot exceed Total - Deposit
                        if (val > maxTransfer) val = maxTransfer;

                        setPayTransfer(val);

                        // When Transfer changes, Cash defaults to remaining (after deposit)
                        setPayCash(Math.max(0, totalDue - deposit - val));
                      }}
                    />
                  </div>
                )}

                {(payMethod === "cash" || payMethod === "split") && (
                  <div className="mb-2">
                    <label className="form-label form-label-sm">รับเงินสด (฿)</label>
                    <input
                      type="number"
                      className={`form-control form-control-lg text-end ${activePayInput === 'cash' ? 'border-primary shadow-sm' : ''}`}
                      value={payCash}
                      onFocus={() => setActivePayInput('cash')}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        setPayCash(val);
                        // Cash change does NOT affect Transfer anymore (Transfer is anchor)
                      }}
                    />
                    {Number(payCash) > 0 && (
                      <div className="text-end mt-1 small text-muted">
                        เงินทอน: <span className="fw-bold text-dark">{formatMoney(Math.max(Number(payCash) - Math.max((payMode === "existing" && payTargetOrder ? Number(payTargetOrder.total_amount) : Number(billTotals.finalTotal)) - Number(payDeposit) - (Number(payMethod === "split" ? payTransfer : 0) || 0), 0), 0))}</span> ฿
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-between gap-2 mt-auto">
                <button className="btn btn-outline-secondary flex-fill" onClick={closePayModal}>ยกเลิก</button>
                <button
                  className="btn btn-primary flex-fill"
                  onClick={handleConfirmPay}
                  disabled={saving || isPayingRef.current || !!payingOrderId || (payMode === "existing" && payTargetOrder && payingOrderId === payTargetOrder.order_id)}
                  style={{ height: '45px', fontSize: '1.1rem' }}
                >
                  {saving || isPayingRef.current || payingOrderId || (payMode === "existing" && payTargetOrder && payingOrderId === payTargetOrder.order_id) ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      กำลังบันทึก...
                    </>
                  ) : (
                    "ยืนยันรับชำระ"
                  )}
                </button>
              </div>
            </div>

            {/* Right Side: Calculator */}
            <div className="p-3 bg-light" style={{ width: '50%' }}>
              <div className="d-flex flex-wrap h-100 gap-2 align-content-start">
                {['7', '8', '9', '4', '5', '6', '1', '2', '3', '0', 'C', '⌫'].map((btn) => (
                  <button
                    key={btn}
                    type="button"
                    className={`btn ${btn === 'C' ? 'btn-danger' : btn === '⌫' ? 'btn-warning' : 'btn-white border'} shadow-sm fw-bold fs-4`}
                    style={{ width: '31%', height: '23%', flexGrow: 1 }}
                    disabled={payMethod === "transfer"}
                    onClick={() => {
                      if (payMethod === "transfer" && activePayInput !== 'deposit') return;

                      // Calculator Logic
                      const targetSetter = activePayInput === 'cash' ? setPayCash : (activePayInput === 'transfer' ? setPayTransfer : setPayDeposit);
                      const currentValue = activePayInput === 'cash' ? payCash : (activePayInput === 'transfer' ? payTransfer : payDeposit);
                      const totalDue = payMode === "existing" && payTargetOrder
                        ? Number(payTargetOrder.total_amount) || 0
                        : Number(billTotals.finalTotal) || 0;

                      let nextVal = String(currentValue);

                      if (btn === 'C') {
                        nextVal = "0";
                      } else if (btn === '⌫') {
                        nextVal = nextVal.slice(0, -1) || "0";
                      } else {
                        if (nextVal === "0") nextVal = btn;
                        else nextVal += btn;
                      }

                      let numVal = Number(nextVal);

                      // Apply Logic based on input type
                      if (activePayInput === 'transfer') {
                        const totalDue = payMode === "existing" && payTargetOrder
                          ? Number(payTargetOrder.total_amount) || 0
                          : Number(billTotals.finalTotal) || 0;
                        const maxTransfer = Math.max(0, totalDue - Number(payDeposit));

                        if (numVal > maxTransfer) numVal = maxTransfer;

                        setPayTransfer(numVal);

                        if (payMethod === "split") {
                          setPayCash(Math.max(0, maxTransfer - numVal));
                        }
                      } else {
                        // Cash (Default or explicit)
                        setPayCash(numVal);
                      }
                    }}
                  >
                    {btn}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Customer Selection Modal */}
      {showCustomerModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-people-fill me-2" style={{ color: "#d4af37" }}></i>เลือกสมาชิก VIP
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowCustomerModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="ค้นหาชื่อ, เบอร์โทร, Member ID..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="table-responsive" style={{ maxHeight: "400px" }}>
                  <table className="table table-hover align-middle">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th style={{ width: "60px" }}></th>
                        <th>Member ID</th>
                        <th>ชื่อลูกค้า</th>
                        <th>เบอร์โทร</th>
                        <th>Line ID</th>
                        <th className="text-end">เลือก</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingCustomers ? (
                        <tr><td colSpan="6" className="py-3"><SkeletonLoader type="table" rows={5} columns={6} /></td></tr>
                      ) : customers.filter(c =>
                        c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
                        (c.phone && c.phone.includes(customerSearch)) ||
                        (c.member_id && c.member_id.toLowerCase().includes(customerSearch.toLowerCase()))
                      ).length === 0 ? (
                        <tr><td colSpan="6" className="text-center py-3 text-muted">ไม่พบข้อมูลลูกค้า</td></tr>
                      ) : (
                        currentVipCustomers.map(c => (
                          <tr key={c.id} style={{ cursor: "pointer" }} onClick={() => handleSelectCustomer(c)}>
                            <td>
                              {c.image_url ? (
                                <img src={c.image_url} alt={c.name} className="rounded-circle" style={{ width: "32px", height: "32px", objectFit: "cover" }} />
                              ) : (
                                <div className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center" style={{ width: "32px", height: "32px" }}>
                                  <i className="bi bi-person-fill"></i>
                                </div>
                              )}
                            </td>
                            <td><span className="badge bg-light text-dark border">{c.member_id || "-"}</span></td>
                            <td className="fw-bold">{c.name}</td>
                            <td>{c.phone || "-"}</td>
                            <td>{c.line_id || "-"}</td>
                            <td className="text-end">
                              <button className="btn btn-sm btn-primary">
                                เลือก
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {!loadingCustomers && filteredVipCustomers.length > 0 && (
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <small className="text-muted">
                      แสดง {filteredVipCustomers.length === 0 ? 0 : startVipIndex + 1}-{endVipIndex} จาก {filteredVipCustomers.length}
                    </small>
                    <div className="btn-group btn-group-sm">
                      <button
                        className="btn btn-outline-secondary"
                        disabled={customerPage <= 1}
                        onClick={() => setCustomerPage((p) => Math.max(1, p - 1))}
                      >
                        <i className="bi bi-chevron-left"></i>
                      </button>
                      <span className="btn btn-outline-secondary disabled">หน้า {customerPage}/{totalCustomerPages}</span>
                      <button
                        className="btn btn-outline-secondary"
                        disabled={customerPage >= totalCustomerPages}
                        onClick={() => setCustomerPage((p) => Math.min(totalCustomerPages, p + 1))}
                      >
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal ยืนยันยกเลิกบิล (ตรงกลางจอ) */}
      {cancelOrderModal.isOpen && cancelOrderModal.order && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1060 }}
          onClick={handleCloseCancelModal}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: "440px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content shadow-lg border-0 rounded-4 overflow-hidden">
              <div className="modal-header bg-danger bg-opacity-10 border-0 pt-3 pb-2 px-4">
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center bg-danger text-white shadow-sm"
                    style={{ width: "36px", height: "36px", flexShrink: 0 }}
                  >
                    <i className="bi bi-exclamation-triangle-fill fs-5"></i>
                  </div>
                  <div>
                    <h5 className="modal-title fw-bold text-danger mb-0">ยืนยันการยกเลิกบิล</h5>
                    <small className="text-muted">โปรดตรวจสอบรายละเอียดก่อนยืนยัน</small>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  disabled={cancelOrderModal.isCanceling}
                  onClick={handleCloseCancelModal}
                  aria-label="Close"
                ></button>
              </div>

              <div className="modal-body px-4 py-3">
                <div className="p-3 bg-light rounded-3 mb-3 border">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted small">เลขที่บิล:</span>
                    <strong className="text-dark font-monospace">{cancelOrderModal.order.order_code}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted small">ชื่อลูกค้า:</span>
                    <span className="fw-semibold text-dark">{cancelOrderModal.order.customer_name || "-"}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-0">
                    <span className="text-muted small">ยอดรวมสุทธิ:</span>
                    <span className="fw-bold text-primary">฿{formatMoney(cancelOrderModal.order.total_amount)}</span>
                  </div>
                </div>

                <div className="alert alert-warning py-2 px-3 small mb-0 d-flex align-items-center gap-2">
                  <i className="bi bi-info-circle-fill text-warning fs-5 flex-shrink-0"></i>
                  <div>
                    เมื่อยกเลิกแล้ว บิลนี้จะไม่ถูกนำไปคำนวณยอดขาย และยอดเงินมัดจำ (ถ้ามี) จะถูกคืนกลับสู่ระบบ
                  </div>
                </div>
              </div>

              <div className="modal-footer border-0 px-4 pb-4 pt-1 d-flex justify-content-end gap-2 bg-white">
                <button
                  type="button"
                  className="btn btn-light px-4 border"
                  disabled={cancelOrderModal.isCanceling}
                  onClick={handleCloseCancelModal}
                >
                  ไม่ยกเลิก
                </button>
                <button
                  type="button"
                  className="btn btn-danger px-4 shadow-sm fw-bold d-flex align-items-center gap-2"
                  disabled={cancelOrderModal.isCanceling}
                  onClick={handleConfirmCancelOrder}
                >
                  {cancelOrderModal.isCanceling ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      กำลังยกเลิก...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-trash3-fill"></i>
                      ยืนยันยกเลิกบิล
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionsPage;
